/**
 * 导演模式系统配置（管理后台）
 *
 * 含限流、超时、worker 间隔、水印等参数
 */

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import toast from '../../Toast';
import { systemMediaAPI } from '../../../services/api';
import {
  directorAdminApi,
  type DirectorSystemConfig,
} from '../../../apps/director/services/directorAdminApi';

interface NumField {
  key: keyof DirectorSystemConfig;
  label: string;
  hint?: string;
  unit?: string;
}

const PROJECT_FIELDS: NumField[] = [
  { key: 'maxShotsPerProject', label: '单项目最大分镜数', hint: '默认 30' },
];

const CONCURRENCY_FIELDS: NumField[] = [
  { key: 'maxKeyframeCandidatesPerUser', label: 'keyframe 候选并发上限（每用户）' },
  { key: 'maxCompositionsPerUser', label: '成片合成并发上限（每用户）' },
  { key: 'shotSplitRateLimitPerMinute', label: '智能拆分镜限频', unit: '次/分钟' },
];

const TIMEOUT_FIELDS: NumField[] = [
  {
    key: 'compositionTimeoutSeconds',
    label: '成片合成总超时',
    unit: '秒',
    hint: '导演模式成片合成的最长等待时间，包含 ffmpeg 所有步骤',
  },
  {
    key: 'ffmpegStepTimeoutSeconds',
    label: 'ffmpeg 单步超时',
    unit: '秒',
    hint: 'ffmpeg 每个单独操作（裁剪、拼接、转码）的最大等待时间',
  },
];

const WORKER_FIELDS: NumField[] = [
  // 图片 worker 扫描间隔属平台图片运行时配置，编辑入口已迁移至「图片配置」tab
  { key: 'compositionWorkerIntervalMs', label: '成片 worker 扫描间隔', unit: 'ms' },
];

const SHARE_FIELDS: NumField[] = [
  { key: 'shareLinkDefaultExpiresInDays', label: '分享链接默认有效期', unit: '天' },
];

export default function DirectorSystemConfigTab() {
  const [config, setConfig] = useState<DirectorSystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    directorAdminApi
      .getSystemConfig()
      .then((cfg) => setConfig(cfg))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const update = <K extends keyof DirectorSystemConfig>(key: K, value: DirectorSystemConfig[K]) => {
    setConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const patch: Partial<DirectorSystemConfig> = {
        maxShotsPerProject: config.maxShotsPerProject,
        maxKeyframeCandidatesPerUser: config.maxKeyframeCandidatesPerUser,
        maxCompositionsPerUser: config.maxCompositionsPerUser,
        shotSplitRateLimitPerMinute: config.shotSplitRateLimitPerMinute,
        compositionTimeoutSeconds: config.compositionTimeoutSeconds,
        ffmpegStepTimeoutSeconds: config.ffmpegStepTimeoutSeconds,
        compositionWorkerIntervalMs: config.compositionWorkerIntervalMs,
        shareLinkDefaultExpiresInDays: config.shareLinkDefaultExpiresInDays,
        siteWatermarkEnabled: config.siteWatermarkEnabled,
        siteWatermarkLogoStorageKey: config.siteWatermarkLogoStorageKey,
        fontPath: config.fontPath,
      };
      const updated = await directorAdminApi.updateSystemConfig(patch);
      setConfig(updated);
      toast.success('已保存');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载中
      </div>
    );
  }

  const renderNumberField = (f: NumField) => (
    <div key={f.key as string}>
      <label className="block text-sm font-medium mb-1">
        {f.label}
        {f.unit && <span className="text-xs text-light-muted ml-2">（{f.unit}）</span>}
      </label>
      <input
        type="number"
        min={0}
        value={config[f.key] as number}
        onChange={(e) => update(f.key, parseInt(e.target.value, 10) || 0)}
        className="w-full input text-sm"
      />
      {f.hint && <p className="text-xs text-light-muted mt-1">{f.hint}</p>}
    </div>
  );

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="card p-5">
        <h3 className="text-base font-semibold mb-3">项目限制</h3>
        <div className="grid sm:grid-cols-2 gap-4">{PROJECT_FIELDS.map(renderNumberField)}</div>
      </div>

      <div className="card p-5">
        <h3 className="text-base font-semibold mb-3">并发与限流</h3>
        <div className="grid sm:grid-cols-3 gap-4">{CONCURRENCY_FIELDS.map(renderNumberField)}</div>
      </div>

      <div className="card p-5">
        <h3 className="text-base font-semibold mb-1">导演模式超时</h3>
        <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 leading-relaxed">
          以下仅控制导演模式成片的合成超时。全局图片生成超时请在 <strong>图片管理 → 图片配置</strong> 中设置。
        </div>
        <div className="grid sm:grid-cols-2 gap-4">{TIMEOUT_FIELDS.map(renderNumberField)}</div>
      </div>

      <div className="card p-5">
        <h3 className="text-base font-semibold mb-3">Worker</h3>
        <div className="grid sm:grid-cols-2 gap-4">{WORKER_FIELDS.map(renderNumberField)}</div>
      </div>

      <div className="card p-5 space-y-4">
        <h3 className="text-base font-semibold">站点水印</h3>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.siteWatermarkEnabled}
            onChange={(e) => update('siteWatermarkEnabled', e.target.checked)}
          />
          <span className="text-sm">启用站点水印</span>
        </label>
        <div>
          <label className="block text-sm font-medium mb-1">水印 logo storage key</label>
          <div className="flex items-center gap-2">
            <input
              value={config.siteWatermarkLogoStorageKey ?? ''}
              onChange={(e) => update('siteWatermarkLogoStorageKey', e.target.value || null)}
              placeholder="例如 images/system/director-watermark.png"
              className="flex-1 input text-sm font-mono"
            />
            <label className="btn-secondary px-3 py-1.5 text-xs cursor-pointer">
              上传 logo
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const result = await systemMediaAPI.upload(file, 'director_watermark');
                    update('siteWatermarkLogoStorageKey', result.storageKey);
                    toast.success('水印 logo 已上传');
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : '上传失败');
                  } finally {
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>
          <p className="text-xs text-light-muted mt-1">
            上传后会自动填入 storage key 并保存。也可以手动填写已存在的 key。
          </p>
        </div>
      </div>

      <div className="card p-5">
        <h3 className="text-base font-semibold mb-3">字体（ffmpeg 文字烧录）</h3>
        <div>
          <label className="block text-sm font-medium mb-1">字体绝对路径</label>
          <input
            value={config.fontPath ?? ''}
            onChange={(e) => update('fontPath', e.target.value || null)}
            placeholder="/usr/share/fonts/wqy-zenhei/wqy-zenhei.ttc"
            className="w-full input text-sm font-mono"
          />
          <p className="text-xs text-light-muted mt-1">
            生产服务器需安装 CJK 字体后填路径。第一期不做字幕，但 ffmpeg 烧录文字水印时也会用到。
          </p>
        </div>
      </div>

      <div className="card p-5">
        <h3 className="text-base font-semibold mb-3">分享</h3>
        <div className="grid sm:grid-cols-2 gap-4">{SHARE_FIELDS.map(renderNumberField)}</div>
      </div>

      <div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary px-5 py-2 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}
